// Imports React y firebase
import React, { useCallback, useContext, useEffect, useState } from "react";
import Select, { components } from 'react-select';
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase";
// Imports Contexts
import { AppContext } from "../../../contexts/AppContext";
import { AuthContext } from "../../../contexts/AuthContext";
import staticData from '../../../contexts/staticData.json';
// Imports Helpers
import { formatCurrency, getSubventionNames2 } from '../../../shared/helpers';
// Imports Components
import ContainerSearch from "../addPME/ContainerSearch";
import { ConfirmationDialog } from "./ConfirmationDialog";
import SalaryExpenseDetailModal from "./SalaryExpenseDetailModal";
import MessageConfirmation from "../../messageConfirmation/MessageConfirmation";
// Imports Icons
import arrowSide from '../../../assets/icons/arrowSide.svg';
import downArrow from '../../../assets/icons/downArrow.svg';
import warning from '../../../assets/icons/infoWhite.svg';
// Import CSS
import './salaryExpenses.css';
//Constantes
const subsidyNames = {
    general: 'General',
    sep: 'SEP',
    pie: 'PIE',
    eib: 'EIB',
    maintenance: 'Mantenimiento',
    retention: 'Pro Retención',
    internship: 'Internado',
    reinforcement: 'Reforzamiento',
    faep: 'FAEP'
};

function SalaryExpenses() {
    //Contextos
    const { dataProjects, establishmentList, pmeList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);
    //Estados locales
    const [dataSearch, setDataSearch] = useState({
        region: null,
        commune: null,
        holder: { value: null, id: null },
        establishment: { value: null, id: null },
    });
    const [pme, setPme] = useState(null);
    const [view, setView] = useState(false);
    const [availablePMEs, setAvailablePMEs] = useState([]);
    const [selectedPMEYear, setSelectedPMEYear] = useState(null);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [total, setTotal] = useState(0);
    const [validationError, setValidationError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showFullExpenseDetail, setShowExpenseDetail] = useState(false);
    const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [pendingExpenseData, setPendingExpenseData] = useState(null);
    const [formData, setFormData] = useState({
        periodType: null,
        selectedMonth: null,
        selectedSubsidies: [],
        expenses: {},
        distributedExpenses: {}
    });

    //Funciones de ayuda
    const getEstablishment = () => {
        const establishmentId = (userData && userData?.dimension === 'Establecimiento')
            ? userData.establishmentId
            : dataSearch.establishment.id;

        return establishmentList.find(establishment => establishment.id === establishmentId);
    };

    const getPMEActivities = useCallback(() => {
        if (!pme) return [];
        const allActivities = pme.activities || [];
        const remunerationsActivities = allActivities.filter(activity =>
            activity.subdimension === 'Remuneraciones'
        );

        return remunerationsActivities.map(activity => ({
            value: activity.id,
            label: activity.nameOfAction,
            activity: activity // guardamos la actividad completa por si necesitamos más datos
        }));
    }, [pme]);

    const getValidationError = () => {
        if (!formData.periodType) {
            return 'Debe seleccionar un tipo de período';
        }
        if (formData.periodType.value === 'monthly' && !formData.selectedMonth) {
            return 'Debe seleccionar un mes para el período mensual';
        }
        if (!selectedActivity) {
            return 'Debe seleccionar una actividad del PME';
        }
        // Validar que la suma de gastos sea mayor a 0
        const expensesSum = Object.values(formData.expenses).reduce((sum, value) => sum + (value || 0), 0);
        if (expensesSum <= 0) {
            return 'El total de gastos debe ser mayor a 0';
        }

        return '';
    };

    const isFormValid = () => {
        return getValidationError() === '';
    };

    const initializeEmptyMonths = () => {
        const emptyMonths = {};
        for (let i = 1; i <= 12; i++) {
            emptyMonths[i] = {};
        }
        return emptyMonths;
    };

    const calculateMonthlyDistribution = () => {
        const distribution = initializeEmptyMonths();

        switch (formData.periodType.value) {
            case 'monthly': {
                distribution[formData.selectedMonth.value] = { ...formData.expenses };
                break;
            }
            case 'accumulated': {
                const currentMonth = new Date().getMonth() + 1;

                // Encontrar el primer mes vacío
                let startMonth = 1;
                for (let month = 1; month <= currentMonth; month++) {
                    if (!pme.salaryExpense?.[month] || Object.keys(pme.salaryExpense[month]).length === 0) {
                        startMonth = month;
                        break;
                    }
                }

                // Calcular cantidad de meses para la distribución
                const monthCount = currentMonth - startMonth + 1;

                if (monthCount > 0) {
                    Object.entries(formData.expenses).forEach(([code, amount]) => {
                        const monthlyAmount = Math.floor(amount / monthCount);
                        const remainder = amount % monthCount;

                        for (let month = startMonth; month <= currentMonth; month++) {
                            distribution[month][code] = monthlyAmount + (month === startMonth ? remainder : 0);
                        }
                    });
                }
                break;
            }
            case 'annual': {
                Object.entries(formData.expenses).forEach(([code, amount]) => {
                    const monthlyAmount = Math.floor(amount / 12);
                    const remainder = amount % 12;

                    for (let month = 1; month <= 12; month++) {
                        distribution[month][code] = monthlyAmount + (month === 1 ? remainder : 0);
                    }
                });
                break;
            }
        }

        return distribution;
    };

    const checkForConflicts = (monthlyDistribution) => {
        // Verificar si ya existe un salaryExpense para esta actividad
        const existingActivityExpense = pme.salaryExpense?.[selectedActivity.value]?.monthlyDistribution;

        if (!existingActivityExpense) return false;

        // Revisar si hay meses con datos existentes
        return Object.entries(monthlyDistribution).some(([month, expenses]) => {
            return Object.keys(expenses).length > 0 &&
                Object.keys(existingActivityExpense[month] || {}).length > 0;
        });
    };

    const handleConflictResolution = async (monthlyDistribution) => {
        return new Promise((resolve) => {
            // Filtrar solo los meses que tienen datos en la nueva distribución
            const relevantMonths = Object.keys(monthlyDistribution).filter(
                month => Object.keys(monthlyDistribution[month]).length > 0
            );

            // Obtener datos existentes solo de los meses relevantes
            const existingData = relevantMonths.reduce((acc, month) => {
                const existingActivityExpense = pme.salaryExpense?.[selectedActivity.value]?.monthlyDistribution;
                if (existingActivityExpense?.[month]) {
                    acc[month] = existingActivityExpense[month];
                }
                return acc;
            }, {});

            setPendingExpenseData({
                existingData,
                newData: monthlyDistribution,
                onSum: async () => {
                    setShowConfirmDialog(false);
                    resolve("sum");
                },
                onOverwrite: async () => {
                    setShowConfirmDialog(false);
                    resolve("overwrite");
                },
                onCancel: () => {
                    setShowConfirmDialog(false);
                    resolve("cancel");
                }
            });
            setShowConfirmDialog(true);
        });
    };

    const resetFormData = () => {
        setFormData({
            periodType: null,
            selectedMonth: null,
            selectedSubsidies: [],
            expenses: {},
            distributedExpenses: {}
        });
        setSelectedActivity(null);
        setTotal(0);
        setValidationError('');
    };

    const handleMessageComplete = () => {
        setShowConfirmationMessage(false);
        setShowErrorMessage(false);
    };
    
    const updatePMEData = useCallback(() => {
        if (!pmeList || !establishmentList) return;
        const currentYear = new Date().getFullYear();
        const establishmentId = userData?.dimension === 'Establecimiento'
            ? userData?.establishmentId
            : dataSearch?.establishment?.id;

        if (establishmentId) {
            if (userData?.permissions === 'admin' || userData?.permissions === 'admin-holder') {
                const establishmentPMEs = pmeList.filter(pme =>
                    pme.establishmentId === establishmentId
                );

                setAvailablePMEs(establishmentPMEs);

                // Si hay PMEs disponibles para este establecimiento
                if (establishmentPMEs.length > 0) {
                    if (!selectedPMEYear) {
                        // Intentar encontrar el PME del año actual
                        const currentPME = establishmentPMEs.find(pme => pme.yearPME === currentYear);

                        if (currentPME) {
                            // Si existe PME del año actual, usarlo
                            setPme(currentPME);
                            setSelectedPMEYear({
                                value: currentPME.yearPME,
                                label: `PME ${currentPME.yearPME}`
                            });
                        } else {
                            // Si no existe PME del año actual, usar el más reciente
                            const latestPME = establishmentPMEs.reduce((latest, current) =>
                                current.yearPME > latest.yearPME ? current : latest
                            );
                            setPme(latestPME);
                            setSelectedPMEYear({
                                value: latestPME.yearPME,
                                label: `PME ${latestPME.yearPME}`
                            });
                        }
                        setView(true);
                    }
                    return;
                }

                // Si no hay PMEs, limpiar estados
                setPme(null);
                setSelectedPMEYear(null);
                setView(false);
                return;
            }

            // Para usuarios no admin
            const updatedPME = pmeList.find(pme =>
                pme.establishmentId === establishmentId &&
                pme.yearPME === currentYear
            );

            if (updatedPME) {
                setPme(updatedPME);
                setView(true);
            } else {
                setPme(null);
                setView(false);
            }
        }
    }, [pmeList, establishmentList, userData, dataSearch?.establishment?.id, selectedPMEYear]);

    useEffect(() => {
        if (userData) {
            updatePMEData();
        }
    }, [updatePMEData, userData]);

    useEffect(() => {
        const error = getValidationError();
        setValidationError(error);
    }, [formData.periodType, formData.selectedMonth, formData.selectedSubsidies, selectedActivity, formData.expenses]);

    useEffect(() => {
        const sum = Object.values(formData.expenses).reduce((acc, curr) => acc + (curr || 0), 0);
        setTotal(sum);
    }, [formData.expenses]);

    //Funciones de manejo de eventos
    const handleSelect = (selectedOption, { name }) => {
        const value = selectedOption ? selectedOption.value : null;
        const id = selectedOption ? selectedOption.id : null;
        console.log(value);
        if (name === 'establishment') {
            setPme(null);
            setDataSearch(prevState => ({
                ...prevState,
                establishment: { value, id }
            }));
            // Si hay un nuevo establecimiento seleccionado, actualizar datos
            if (id) {
                setTimeout(() => {
                    updatePMEData();
                    setSelectedPMEYear(null);
                }, 0);
            } else {
                setView(false);
            }
        } else {
            setDataSearch(prevState => ({
                ...prevState,
                [name]: name === 'holder' || name === 'establishment'
                    ? { value, id }
                    : value,
                ...(name === 'region' && { commune: null, holder: { value: null, id: null }, establishment: null }),
                ...(name === 'commune' && { holder: { value: null, id: null }, establishment: null }),
                ...(name === 'holder' && { establishment: null })
            }));
            setView(false);
        }
    };
    const handleButtonClick = () => {
        updatePMEData();
    };
    const handlePMEChange = (option) => {
        setSelectedPMEYear(option);
        resetFormData();

        if (option) {
            // Buscar el PME correspondiente al año seleccionado
            const selectedPME = availablePMEs.find(pme => pme.yearPME === option.value);
            if (selectedPME) {
                setPme(selectedPME);
                setView(true);
            }
        }
    };

    const handlePeriodTypeChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            periodType: selectedOption,
            selectedMonth: null,
            distributedExpenses: {} // Reset distributed expenses
        }));
    };
    const handleMonthChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            selectedMonth: selectedOption
        }));
    };
    const handleExpenseChange = (code, value) => {
        // Remover caracteres no numéricos excepto punto
        const cleanValue = value.replace(/[$\s.]/g, '');

        // Convertir a número o usar 0 si está vacío
        const numericValue = cleanValue === '' ? 0 : parseInt(cleanValue, 10);

        // Actualizar solo si es un número válido
        if (!isNaN(numericValue)) {
            setFormData(prev => ({
                ...prev,
                expenses: {
                    ...prev.expenses,
                    [code]: numericValue
                }
            }));
        }
    };
    const handleActivityChange = (selectedOption) => {
        setSelectedActivity(selectedOption);

        // Obtener las subvenciones de la actividad seleccionada
        if (selectedOption) {
            const subsidyOptions = getSubventionNames2(selectedOption.activity.subsidies);
            setFormData(prev => ({
                ...prev,
                selectedSubsidies: subsidyOptions
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                selectedSubsidies: []
            }));
        }
    };

    const updatePMEFirestore = async (pmeId, salaryExpense, activities) => {
        try {
            const pmeDocRef = doc(db, 'pme', pmeId);

            //Actualizar salaryExpense y Activities
            await updateDoc(pmeDocRef, {
                salaryExpense: salaryExpense,
                activities: activities
            });
            return true;
        } catch (error) {
            console.error('Error al actualizar PME en firestore:', error);
            throw error;
        }
    };

    const saveChanges = async (monthlyDistribution, isMerging, resolutionType = 'sum') => {
        try {
            const activityId = selectedActivity.value;
            const activitySubsidies = formData.selectedSubsidies;

            // Estructura inicial de salaryExpense
            let newSalaryExpense = { ...(pme.salaryExpense || {}) };

            // Función para calcular total anterior
            const calculatePreviousTotal = (monthlyDistribution) => {
                const monthsToOverwrite = Object.keys(monthlyDistribution).filter(
                    month => Object.keys(monthlyDistribution[month]).length > 0
                );

                return monthsToOverwrite.reduce((sum, month) => {
                    const existingActivityExpense = pme.salaryExpense?.[activityId]?.monthlyDistribution?.[month];
                    if (existingActivityExpense) {
                        const codesToOverwrite = Object.keys(monthlyDistribution[month]);
                        return sum + codesToOverwrite.reduce((monthSum, code) =>
                            monthSum + (existingActivityExpense[code] || 0),
                            0
                        );
                    }
                    return sum;
                }, 0);
            };

            if (isMerging) {
                switch (resolutionType) {
                    case 'sum': {
                        // Fusionar distribución mensual para la actividad
                        const existingDistribution = newSalaryExpense[activityId]?.monthlyDistribution || {};
                        newSalaryExpense[activityId] = {
                            activitySubsidies: activitySubsidies,
                            monthlyDistribution: Object.keys(monthlyDistribution).reduce((acc, month) => {
                                acc[month] = {
                                    ...(existingDistribution[month] || {}),
                                    ...Object.keys(monthlyDistribution[month]).reduce((innerAcc, code) => {
                                        innerAcc[code] = (existingDistribution[month]?.[code] || 0) + monthlyDistribution[month][code];
                                        return innerAcc;
                                    }, {})
                                };
                                return acc;
                            }, {})
                        };
                        break;
                    }
                    case 'overwrite': {
                        const previousTotal = calculatePreviousTotal(monthlyDistribution);

                        // Crear una copia profunda de la distribución existente
                        const existingDistribution = JSON.parse(JSON.stringify(
                            newSalaryExpense[activityId]?.monthlyDistribution || {}
                        ));

                        // Sobreescribir solo los códigos específicos en los meses seleccionados
                        newSalaryExpense[activityId] = {
                            activitySubsidies: activitySubsidies,
                            monthlyDistribution: Object.keys(monthlyDistribution).reduce((acc, month) => {
                                // Mantener la distribución existente del mes
                                acc[month] = { ...(existingDistribution[month] || {}) };

                                // Sobreescribir solo los códigos específicos del mes
                                Object.keys(monthlyDistribution[month]).forEach(code => {
                                    acc[month][code] = monthlyDistribution[month][code];
                                });

                                return acc;
                            }, {})
                        };

                        // Ajustar actividades con los cambios de total
                        const updatedActivities = pme.activities.map(act =>
                            act.id === activityId
                                ? {
                                    ...act,
                                    accruedBalance: act.accruedBalance - previousTotal + total,
                                    effectiveExpense: act.effectiveExpense + previousTotal - total,
                                    effectiveBalance: act.effectiveBalance + previousTotal - total
                                }
                                : act
                        );

                        const updatedPme = {
                            ...pme,
                            activities: updatedActivities,
                            salaryExpense: newSalaryExpense
                        };

                        setPme(updatedPme);
                        return updatedPme;
                    }
                }
            } else {
                // Caso sin fusión
                newSalaryExpense[activityId] = {
                    activitySubsidies: activitySubsidies,
                    monthlyDistribution: monthlyDistribution
                };
            }

            // Ajustar actividades para casos sin sobreescritura
            const updatedActivities = pme.activities.map(act =>
                act.id === activityId
                    ? {
                        ...act,
                        accruedBalance: act.accruedBalance + total,
                        effectiveExpense: act.effectiveExpense - total,
                        effectiveBalance: act.effectiveBalance - total
                    }
                    : act
            );

            const updatedPme = {
                ...pme,
                activities: updatedActivities,
                salaryExpense: newSalaryExpense
            };

            setPme(updatedPme);
            return updatedPme;
        } catch (error) {
            console.error('Error al guardar los cambios:', error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) { return }

        try {
            const monthlyDistribution = calculateMonthlyDistribution();

            const hasConflicts = checkForConflicts(monthlyDistribution);
            if (hasConflicts) {
                const resolution = await handleConflictResolution(monthlyDistribution);
                if (resolution === "cancel") return;

                setIsLoading(true);
                try {
                    const updatedPME = await saveChanges(monthlyDistribution, true, resolution);

                    if (updatedPME) {
                        // Guardar en Firestore
                        await updatePMEFirestore(
                            updatedPME.id,
                            updatedPME.salaryExpense,
                            updatedPME.activities
                        );
                        setShowConfirmationMessage(true);
                        resetFormData();
                    }
                } catch (error) {
                    setShowErrorMessage(true);
                    console.error("Error al guardar los datos:", error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(true);
                try {
                    const updatedPME = await saveChanges(monthlyDistribution, false);

                    if (updatedPME) {
                        // Guardar en Firestore
                        await updatePMEFirestore(
                            updatedPME.id,
                            updatedPME.salaryExpense,
                            updatedPME.activities
                        );

                        setShowConfirmationMessage(true);
                        resetFormData();
                    }
                } catch (error) {
                    setShowErrorMessage(true);
                    console.error("Error al guardar los datos:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        } catch (error) {
            console.error("Error al guardar los datos:", error);
            setShowErrorMessage(true);
        }
    };

    return (
        <section className='salary-expenses-container flex'>
            <ContainerSearch
                dataSearch={dataSearch}
                setDataSearch={setDataSearch}
                handleSelect={handleSelect}
                handleButtonClick={handleButtonClick}
                dataProjects={dataProjects}
                establishmentList={establishmentList}
            />
            <section className='box-form-salary flex'>
                {(view && pme)
                    ? (
                        <>
                            {((dataSearch?.establishment?.value || userData?.establishmentId) && getEstablishment()) && (
                                <article className='info-establishment flex'>
                                    <span className='flex'>
                                        <h3>{getEstablishment().nameEstablishment}</h3>
                                        <img src={arrowSide} className='icon' alt='Icono de flecha' />
                                        <p>{`Gastos de Remuneraciones año ${pme?.yearPME}`}</p>
                                    </span>
                                    {(userData?.permissions === 'admin' || userData?.permissions === 'admin-holder') && (
                                        <Select
                                            options={availablePMEs
                                                .sort((a, b) => b.yearPME - a.yearPME) // Ordenar de mayor a menor año
                                                .slice(0, userData?.permissions === 'admin-holder' ? 2 : undefined) // Tomar solo los 2 primeros si es admin-holder
                                                .map(pme => ({
                                                    value: pme.yearPME,
                                                    label: `PME ${pme.yearPME}`
                                                }))}
                                            value={selectedPMEYear}
                                            onChange={handlePMEChange}
                                            placeholder="Seleccionar Año PME"
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            components={{ DropdownIndicator }}
                                            isClearable
                                        />
                                    )}
                                </article>
                            )}
                            <form className='form-salary-expenses flex'>
                                <h3>Formulario Ingreso Gasto de Remuneraciones</h3>
                                <span className='input-text flex'>
                                    <label htmlFor='establishment'>Establecimiento:</label>
                                    <input type='text' className='input flex' value={getEstablishment()?.nameEstablishment} readOnly />
                                </span>
                                <span className='select flex'>
                                    <label htmlFor='activitiesPME'>Actividad PME:</label>
                                    <Select
                                        options={getPMEActivities()}
                                        value={selectedActivity}
                                        onChange={handleActivityChange}
                                        placeholder='Seleccione una actividad del PME'
                                        className='custom-select'
                                        classNamePrefix='custom-select'
                                        components={{ DropdownIndicator }}
                                        isDisabled={!pme}
                                        noOptionsMessage={() => 'No hay actividades disponibles'}
                                    />
                                </span>
                                {selectedActivity && (
                                    <span className='input-text flex'>
                                        <label htmlFor='subsidies'>Subvenciones:</label>
                                        <input
                                            type='text'
                                            className='input flex'
                                            value={formData.selectedSubsidies}
                                            readOnly
                                        />
                                    </span>
                                )}
                                <span className='select flex'>
                                    <label>Tipo de Período:</label>
                                    <Select
                                        options={staticData.periodOptions}
                                        value={formData.periodType}
                                        onChange={handlePeriodTypeChange}
                                        placeholder='Seleccione tipo de período'
                                        className='custom-select'
                                        classNamePrefix='custom-select'
                                        components={{ DropdownIndicator }}
                                    />
                                </span>
                                {formData.periodType?.value === 'monthly' && (
                                    <span className="select flex">
                                        <label>Mes:</label>
                                        <Select
                                            options={staticData.monthOptions}
                                            value={formData.selectedMonth}
                                            onChange={handleMonthChange}
                                            placeholder="Seleccione mes"
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            components={{ DropdownIndicator }}
                                        />
                                    </span>
                                )}
                                {formData.periodType && (
                                    <article className='expense-categories flex'>
                                        <p>Cuenta SUPEREDUC</p>
                                        <table className='expense-table'>
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Nombre Subcategoría</th>
                                                    <th>Monto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {staticData.categoryExpensesRemunerations.map((category) => (
                                                    <tr key={category.code}>
                                                        <td className='code-cell'>{category.code}</td>
                                                        <td className='name-cell'>{category.name}</td>
                                                        <td className='amount-cell'>
                                                            <input
                                                                type='text'
                                                                value={formData.expenses[category.code]
                                                                    ? formatCurrency(formData.expenses[category.code])
                                                                    : ''
                                                                }
                                                                onChange={(e) => handleExpenseChange(category.code, e.target.value)}
                                                                placeholder="$ 0"
                                                                className='amount-input'
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="2" className="total-label">Total Remuneraciones:</td>
                                                    <td className="total-amount">{formatCurrency(total)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </article>
                                )}
                                <article className='button-container'>
                                    <button
                                        className={`sent-form flex ${isFormValid() ? 'disabled' : ''}`}
                                        disabled={!isFormValid()}
                                        onClick={handleSubmit}
                                    >
                                        <p>Guardar</p>
                                    </button>
                                    {validationError && (
                                        <span className='tooltip-text flex'>
                                            <img src={warning} alt='Icono de Advertencia' />
                                            <p>{validationError}</p>
                                        </span>
                                    )}
                                </article>
                                <button
                                    className='button-show-salary'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowExpenseDetail(true);
                                    }}
                                >
                                    <p>Ver Remuneraciones PME {pme?.yearPME}</p>
                                </button>
                            </form>
                        </>
                    )
                    : (
                        <p className='text-bold message-not-found flex'>No existe PME activo para este establecimiento</p>
                    )}
                {showConfirmDialog && (
                    <ConfirmationDialog
                        existingData={pendingExpenseData.existingData}
                        newData={pendingExpenseData.newData}
                        onSum={pendingExpenseData.onSum}
                        onOverwrite={pendingExpenseData.onOverwrite}
                        onCancel={pendingExpenseData.onCancel}
                    />
                )}
                {showFullExpenseDetail && (
                    <SalaryExpenseDetailModal
                        pme={pme}
                        onClose={setShowExpenseDetail}
                    />
                )}
            </section>
            <MessageConfirmation
                confirmation={showConfirmationMessage}
                error={showErrorMessage}
                view={false}
                onComplete={handleMessageComplete}
            />
        </section>
    )
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={downArrow} alt="CustomArrow" />
        </components.DropdownIndicator>
    );
};


export default SalaryExpenses;